import * as DG from "ui/dialogs"

import { Client, ClientAgencyConfig } from "models/common"
import React, { ReactNode } from "react"
import { css, cx } from "@emotion/css"

import { Autocomplete } from "@material-ui/lab"
import Button from "ui/buttons/Button"
import DropdownIcon from "@material-ui/icons/KeyboardArrowDown"
import { ErrorsMap } from "api/http/utils"
import { TextInput } from "components/inputs/TextInput"
import { useI18n } from "providers/I18n"
import { useLocationSearch } from "hooks/useLocationSearch"
import { useAgencyId } from "hooks/useAgencyId"

export type ModalInfo =
  | {
      clients?: Array<Partial<Client> & Partial<ClientAgencyConfig>> | undefined
      type?: "primary" | "primary_destructive" | "secondary_destructive"
      title?: string
      isTeamLead?: boolean
      description?: ReactNode
      cancelLabel?: string
      selectedClientId?: string
      setSelectedClientId: React.Dispatch<
        React.SetStateAction<string | undefined>
      >
      selectedAgencyId?: string
      setSelectedAgencyId: React.Dispatch<
        React.SetStateAction<string | undefined>
      >
      onSubmit?: (e?: any) => void
      submitLabel?: string
    }
  | undefined

type Props = ModalInfo & {
  open: boolean
  onClose: () => void
}

type Option = {
  label: string | undefined
  value: string | undefined
} | null

function chooseVariant(type: Props["type"]): "outlined" | "contained" {
  switch (type) {
    case "primary":
    case "primary_destructive":
      return "contained"
    case "secondary_destructive":
      return "outlined"
    default:
      return "contained"
  }
}

function chooseColor(type: Props["type"]): "primary" | "destructive" {
  switch (type) {
    case "primary":
      return "primary"
    case "primary_destructive":
      return "destructive"
    case "secondary_destructive":
      return "destructive"
    default:
      return "primary"
  }
}

export default function ImportModal(props: Props) {
  const {
    clients,
    selectedClientId,
    setSelectedClientId,
    setSelectedAgencyId,
  } = props
  const { t } = useI18n()
  const id = "alert-dialog-title"
  const [errors, setErrors] = React.useState<ErrorsMap | undefined>()
  const search = useLocationSearch()
  const { agencyId } = useAgencyId() || search.get("agencyId")

  const options = clients?.map((client) => ({
    label: client?.name,
    value: client?.id,
    agencyId: client?.agencyId || agencyId,
  }))

  const option = options?.find((opt) => opt.value === selectedClientId) || {
    value: "",
    label: "",
    agencyId: "",
  }

  const onSelectChange = (
    option: {
      label: string | undefined
      value: string | undefined
      agencyId: string | undefined
    } | null,
  ) => {
    setSelectedClientId(option?.value)
    setSelectedAgencyId(option?.agencyId)
    setErrors(undefined)
  }

  const handleChange = (
    _event: React.ChangeEvent<{}>,
    option: (Option & { agencyId: string | undefined }) | null,
  ) => onSelectChange(option)

  return (
    <DG.Dialog
      className={styles.dialog}
      open={props.open}
      onClose={props.onClose}
      id={id}
    >
      <DG.DialogHeader>
        <DG.DialogTitle id={id}>{props.title}</DG.DialogTitle>
        <DG.DialogClose onClose={props.onClose} />
      </DG.DialogHeader>

      <DG.DialogContent paddingTop className={styles.content}>
        <p className={cx("BodyBody-1BlackLight", styles.contentText)}>
          {props.description}
        </p>
        <Autocomplete
          placeholder={t("client-groups.list.client")}
          options={options || []}
          value={option}
          onChange={handleChange}
          className={styles.input}
          getOptionLabel={(option) => option.label as string}
          getOptionSelected={(option, value) => option.label === value.label}
          disableClearable={!option?.label}
          popupIcon={<DropdownIcon />}
          renderInput={(prms) => (
            <TextInput
              {...prms}
              name={"label"}
              size="medium"
              margin="normal"
              placeholder={t("client-groups.list.client")}
              error={!!errors}
              InputProps={{
                ...prms.InputProps,
                className: cx("BodyBody-2BlackRegular", styles.selectInput),
                endAdornment: (
                  <React.Fragment>
                    {prms.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      </DG.DialogContent>

      <DG.DialogFooter>
        <DG.DialogActions>
          <Button variant="outlined" color="primary" onClick={props.onClose}>
            {props.cancelLabel ?? t("confirmation.dialog.cancel")}
          </Button>
          <Button
            variant={chooseVariant(props.type)}
            color={chooseColor(props.type)}
            onClick={props?.onSubmit}
            data-testid={`delete-button-${props.type}`}
          >
            {props.submitLabel ?? t("confirmation.dialog.submit")}
          </Button>
        </DG.DialogActions>
      </DG.DialogFooter>
    </DG.Dialog>
  )
}

const styles = {
  dialog: css`
    margin: auto;
    min-width: 448px;
    padding: 0px;
  `,
  content: css`
    padding-top: 0px;
  `,
  contentText: css`
    padding: 0px 0px var(--spacing-m);
  `,
  input: css`
    margin: var(--spacing-xs) 0 var(--spacing-m) 0;
    width: 250px;
  `,
  selectInput: css`
    width: 250px;
    padding-right: var(--spacing-xxl-4);
    .MuiFormControl-marginNormal {
      margin: 0;
    }
    .MuiAutocomplete-endAdornment {
      right: 9px;
    }
  `,
}
